import React, {useEffect, useRef, useState} from "react";
import {Autocomplete, TextField} from "@mui/material";
import {fetchClients, fetchDevices, isCancelled} from "./consoleApi";
import {TYPE_LABELS} from "./consoleModel";
import {ClientStatusPill, OnlinePill} from "./ConsoleParts";

/**
 * One box that finds any device (name, IMEI, unit ID, phone) or client across all clients.
 * onPickClient(clientId); onPickDevice(device).
 */
export function GlobalSearch({onPickClient, onPickDevice}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({clients: [], devices: [], deviceTotal: 0, loading: false});
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults({clients: [], devices: [], deviceTotal: 0, loading: false});
      return undefined;
    }
    const controller = new AbortController();
    setResults((r) => ({...r, loading: true}));
    const timer = setTimeout(async () => {
      try {
        const [clients, devices] = await Promise.all([
          fetchClients({q: term, page: 0, size: 3}, controller.signal),
          fetchDevices({q: term, page: 0, size: 6}, controller.signal),
        ]);
        setResults({clients: clients.rows, devices: devices.rows, deviceTotal: devices.total, loading: false});
      } catch (e) {
        if (!isCancelled(e)) setResults({clients: [], devices: [], deviceTotal: 0, loading: false});
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q]);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pickClient = (id) => {
    setOpen(false);
    setQ("");
    onPickClient(id);
  };
  const pickDevice = (d) => {
    setOpen(false);
    setQ("");
    onPickDevice(d);
  };
  const first = results.clients[0] ? () => pickClient(results.clients[0].client.clientId) : results.devices[0] ? () => pickDevice(results.devices[0]) : null;
  const showPanel = open && q.trim().length >= 2;
  const item = "w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-ink-50 focus:bg-ink-50 focus:outline-none";

  return (
    <div ref={boxRef} className="relative w-full sm:w-96">
      <input
        type="search"
        aria-label="Find a device or client"
        placeholder="Find a device or client: IMEI, name, phone"
        className="w-full h-9 px-3 rounded-md border border-ink-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        value={q}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" && first) {
            e.preventDefault();
            first();
          }
        }}
      />
      {showPanel && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-96 overflow-y-auto bg-white border border-ink-200 rounded-lg shadow-lg py-1">
          {results.loading && results.clients.length === 0 && results.devices.length === 0 && (
            <div className="px-3 py-2 text-sm text-ink-500">Searching…</div>
          )}
          {!results.loading && results.clients.length === 0 && results.devices.length === 0 && (
            <div className="px-3 py-2 text-sm text-ink-500">No device or client matches “{q.trim()}”.</div>
          )}
          {results.clients.length > 0 && (
            <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500">Clients</div>
          )}
          {results.clients.map((r) => (
            <button key={`c${r.client.clientId}`} type="button" className={item} onClick={() => pickClient(r.client.clientId)}>
              <span className="min-w-0">
                <span className="block font-semibold text-ink-900 truncate">{r.client.name}</span>
                <span className="block text-xs text-ink-500">
                  {r.client.city} · {r.devices.toLocaleString("en-IN")} devices
                </span>
              </span>
              <ClientStatusPill status={r.client.status} />
            </button>
          ))}
          {results.devices.length > 0 && (
            <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
              Devices{results.deviceTotal > results.devices.length ? ` · first ${results.devices.length} of ${results.deviceTotal}` : ""}
            </div>
          )}
          {results.devices.map((d) => (
            <button key={`d${d.assetId}`} type="button" className={item} onClick={() => pickDevice(d)}>
              <span className="min-w-0">
                <span className="block font-semibold text-ink-900 truncate">{d.name}</span>
                <span className="block text-xs text-ink-500 truncate">
                  {TYPE_LABELS[d.assetType]} · {d.clientName} · <span className="font-mono">{d.unitId}</span>
                </span>
              </span>
              <OnlinePill device={d} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Searchable dropdown that opens any client directly; clearing it returns to the client list. */
// The app's global :focus-visible ring (tailwind.css) also lands on the text box inside MUI's
// field, drawing a second box inside MUI's own focus border; it stays while the field keeps focus
// after a pick. Drop that inner ring and give MUI's border and label the brand colour instead.
const BRAND = "#4a448a"; // tailwind brand-600
const goToClientSx = {
  width: 280,
  maxWidth: "100%",
  "& .MuiAutocomplete-input:focus, & .MuiAutocomplete-input:focus-visible": {outline: "none", boxShadow: "none"},
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: BRAND},
  "& .MuiInputLabel-root.Mui-focused": {color: BRAND},
};

export function GoToClient({options, clientId, onPick, onClear}) {
  const value = options.find((o) => o.clientId === clientId) || null;
  return (
    <Autocomplete
      size="small"
      sx={goToClientSx}
      options={options}
      value={value}
      getOptionLabel={(o) => (o.status === "ACTIVE" ? o.name : `${o.name} (inactive)`)}
      isOptionEqualToValue={(a, b) => a.clientId === b.clientId}
      onChange={(e, picked) => (picked ? onPick(picked.clientId) : onClear())}
      renderInput={(params) => <TextField {...params} label="Go to client" placeholder="Type a client name" />}
      noOptionsText="No client with that name"
    />
  );
}
